import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { io } from "socket.io-client";

import { useCurrentUser } from "~/context/CurrentUserContext";

import type { PropsWithChildren } from "react";
import type { Socket } from "socket.io-client";

interface WebSocketContextType {
  socket: Socket | null;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }: PropsWithChildren) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const currentUser = useCurrentUser();

  const userId = useMemo(() => currentUser?.id || null, [currentUser?.id]);

  useEffect(() => {
    console.info(
      "\x1b[34m [WebSocketProvider] useEffect triggered, user: \x1b[0m",
      userId || "anonymous",
    );

    const socketInstance = io({
      path: "/api/ws",
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 2_000,
      transports: ["websocket", "polling"],
      autoConnect: true,
      timeout: 10_000,
    });

    socketInstance.onAny((event, ...args) => {
      console.info(`\x1b[34m [Socket.IO] Event: ${event} \x1b[0m`, args);
    });

    socketInstance.on("connect_error", (error) => {
      console.error("[Socket.IO] Connection error:", error);
    });

    socketInstance.on("error", (error) => {
      console.error("[Socket.IO] General error:", error);
    });

    socketInstance.on("connect_timeout", (timeout) => {
      console.error("[Socket.IO] Connection timeout:", timeout);
    });

    socketInstance.on("reconnect_attempt", (attempt) => {
      console.info(`\x1b[34m [Socket.IO] Reconnect attempt: ${attempt} \x1b[0m`);
    });

    socketInstance.on("reconnect_error", (error) => {
      console.error("[Socket.IO] Reconnect error:", error);
    });

    socketInstance.on("reconnect_failed", () => {
      console.error("[Socket.IO] Reconnect failed");
    });

    socketInstance.on("connect", () => {
      console.info("\x1b[34m [Socket.IO] Connected successfully \x1b[0m");
    });

    socketInstance.on("disconnect", (reason) => {
      console.info(`\x1b[34m [Socket.IO] Disconnected: ${reason} \x1b[0m`);
    });

    setSocket(socketInstance);

    return () => {
      console.info("\x1b[34m [Socket.IO] Cleaning up socket connection \x1b[0m");
      socketInstance.disconnect();
      setSocket(null);
    };
  }, [userId]);

  return <WebSocketContext.Provider value={{ socket }}>{children}</WebSocketContext.Provider>;
};
